/**
 * The Approvals board's acts, carried to the platform's approval chain (build 4).
 *
 *   approval.raise  RAISE A REQUEST: a requisition into the chain
 *   approval.step   APPROVE / REJECT: the request's next step, signed by the person signed in
 *   approval.order  ISSUE THE PURCHASE ORDER: the order for an approved request
 *
 * Nothing about who is acting is sent. The board's old role picker said who
 * was signing; on the platform the step is whichever one the request waits
 * on, the signer is whoever is signed in, and whether they hold that step is
 * the platform's to say — in its words, which the notice repeats. Only the
 * platform's board sends these: the demo's sample works as it always did.
 *
 * Nothing here imports the demo domain: this sits in the entry chunk.
 */
import { DataSourceError } from "../../../api/dataSource";
import { APPROVAL_STEPS, type NewApprovalRequest } from "../../../domain/approvals";
import { t } from "../../../i18n";
import { registerWrite } from "../writes";

/** A request as the board holds it (demo/domain/tables.ts REQUESTS, or one fed from the platform). */
export interface RequestRecord {
  id: string;
  type?: string;
  title?: string;
  contractor?: string;
  value?: number;
  why?: string;
  from?: string;
  emergency?: boolean;
}

/** What APPROVE / REJECT hands over. */
export interface StepExtra {
  ok: boolean;
  note?: string;
}

const text = (s?: string): string | undefined => {
  const v = s?.trim();
  return v ? v : undefined;
};

/** The board's request form → the platform's request. Pure, for the tests. */
export function newRequest(rec: RequestRecord): NewApprovalRequest {
  return {
    kind: rec.type ?? "",
    title: rec.title?.trim() ?? "",
    ...(text(rec.contractor) ? { supplier: text(rec.contractor) } : {}),
    /* nothing typed is no value, never a value of nothing — the platform refuses it in words */
    value: typeof rec.value === "number" && rec.value > 0 ? rec.value : null,
    why: rec.why?.trim() ?? "",
    ...(text(rec.from) ? { source: text(rec.from) } : {}),
    emergency: !!rec.emergency,
  };
}

/* the step's name as the chain names it — Engineering, COO — the same in either language */
const label = (key: string): string => APPROVAL_STEPS.find((x) => x.key === key)?.label ?? key;

const approvals = (siteId: string): unknown[][] => [["approvals", siteId]];

/**
 * A refusal in the platform's own sentence — "You raised RQ-2026-0002. Nobody
 * approves their own request." — rather than with the transport's prefix
 * ("400: …", "Not permitted: …") in front of it.
 */
export async function inWords<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof DataSourceError && typeof e.detail === "string" && e.detail.trim())
      throw new DataSourceError(e.detail.trim(), { status: e.status, detail: e.detail, cause: e });
    throw e;
  }
}

registerWrite("approval.raise", {
  invalidate: approvals,
  retryable: true,
  async send(ctx, rec: RequestRecord) {
    const r = await inWords(() => ctx.source.raiseApprovalRequest(ctx.siteId, newRequest(rec), ctx.key));
    return {
      id: r.number,
      reference: r.number,
      kind: "requisition",
      text: t("{ref} raised on the platform. It now waits on Engineering.", { ref: r.number }),
    };
  },
});

registerWrite("approval.step", {
  invalidate: approvals,
  /* the platform signs a step once per key: TRY AGAIN cannot sign it twice */
  retryable: true,
  async send(ctx, rec: RequestRecord, extra: StepExtra) {
    const ack = await inWords(() =>
      ctx.source.signApprovalStep(ctx.siteId, rec.id, extra?.ok ? "approved" : "rejected", text(extra?.note), ctx.key),
    );
    const step = label(ack.step);
    if (ack.decision === "rejected")
      return {
        id: ack.number,
        reference: ack.number,
        kind: "requisition",
        text: t("{ref} rejected at {step}.", { ref: ack.number, step }),
      };
    if (ack.status === "approved")
      return {
        id: ack.number,
        reference: ack.number,
        kind: "requisition",
        text: t("{ref} approved at every step. The purchase order can now be issued.", { ref: ack.number }),
      };
    return {
      id: ack.number,
      reference: ack.number,
      kind: "requisition",
      tone: ack.over ? "warn" : "ok",
      text: ack.over
        ? t("{ref} approved at {step}, above that step's limit. The steps after it will see that.", {
            ref: ack.number,
            step,
          })
        : t("{ref} approved at {step}. It moves to the next step.", { ref: ack.number, step }),
    };
  },
});

registerWrite("approval.order", {
  invalidate: approvals,
  retryable: true,
  async send(ctx, rec: RequestRecord, extra: { supplier?: string } | null) {
    const r = await inWords(() => ctx.source.orderApprovalRequest(ctx.siteId, rec.id, text(extra?.supplier), ctx.key));
    return {
      id: r.number,
      reference: r.number,
      kind: "order",
      text: t("Purchase order {po} issued for {ref}.", { po: r.number, ref: rec.id }),
    };
  },
});
